import React, { useState } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { getUtcDateFormattedFullSmall } from 'utils/timeFormatHelpers';
import { fontSize } from '../../../global/variables';
import withTranslation from '../../../hoc/withTranslation';
import Pagination from './Pagination';
import IdSearchDetail from './IdSearchDetail';
import { getOrdersByActivity } from '../../../utils/OrdersHelpers';

const { small, normal } = fontSize;

const StyledSummary = styled.div`
  margin-bottom: 0.5rem;
`;

export const StyledDetailContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  justify-content: space-between;
  margin-right: 1rem;
  margin-top: -0.8rem;
  margin-bottom: 2rem;
`;

export const StyledTitle = styled.p`
  text-transform: uppercase;
  margin: 0 0.6rem 0 0;
  font-size: ${small};
  opacity: 0.75;
  display: inline-block;
`;

export const StyledDetailsText = styled.strong`
  font-size: ${normal};
  line-height: 2rem;
  display: block;
`;

const IdSearchDetails = ({
  activities,
  helpers: { formatDuration },
  translations: {
    idSearchContainer: {
      sequenceNumber,
      tourIdLabel,
      vehicleIdLabel,
      locationLabel,
      unassignedJobTitle,
      reasonTitle,
      jobTypeLabel,
      tags,
      arrival,
      departure,
      demand,
      durationLabel,
      TW,
      territories,
      skills,
      position,
      priority,
      category,
      group,
    },
  },
}) => {
  const solution = useSelector(({ solution: stateSolution }) => stateSolution);
  const orders = useSelector((state) => state.orders[solution.show]);
  const tourPlanner = useSelector(
    ({ tourPlanner: stateTourPlanner }) => stateTourPlanner.value[solution.show],
  );
  const offset = tourPlanner.offset;
  const solOrders = solution.requests[solution.show].tempOrders || orders;
  const [currentItem, setCurrentItem] = useState(0);

  const activity = activities[currentItem];
  const order = getOrdersByActivity(solOrders, activity)?.[0];
  const timeWindow = order?.TimeWindow?.[0];
  const duration = moment(activity?.time?.departure).diff(
    moment(activity?.time?.arrival),
    'minutes',
  );
  const formattedDuration = duration > 0 && formatDuration(Math.floor(duration));
  const hasLocation = activity?.location?.lat && activity?.location?.lng;

  return (
    <div>
      <Pagination
        numberOfPages={activities.length}
        currentItem={currentItem}
        setCurrentItem={(val) => setCurrentItem(val)}
      />
      {activity?.reasons ? (
        <StyledSummary>
          <IdSearchDetail title={unassignedJobTitle} text={activity?.jobId} />
          <StyledDetailContainer>
            <StyledTitle>{reasonTitle}</StyledTitle>
            {activity?.reasons.map((reason, index) => (
              <StyledDetailsText key={`reason-${index}`}>{reason.description}</StyledDetailsText>
            ))}
          </StyledDetailContainer>
        </StyledSummary>
      ) : (
        <StyledSummary>
          <IdSearchDetail title={sequenceNumber} text={activity?.sequenceNo} />
          {activity?.jobTag && <IdSearchDetail title={tags} text={activity?.jobTag} />}
          <IdSearchDetail
            title={arrival}
            text={getUtcDateFormattedFullSmall(activity?.time.arrival, offset)}
          />
          <IdSearchDetail
            title={departure}
            text={getUtcDateFormattedFullSmall(activity?.time.departure, offset)}
          />
          {formattedDuration && <IdSearchDetail title={durationLabel} text={formattedDuration} />}
          {order?.Group && <IdSearchDetail title={group} text={order.Group} />}
          {timeWindow && (
            <IdSearchDetail
              title={TW}
              text={`${getUtcDateFormattedFullSmall(
                timeWindow?.[0],
                offset,
              )}-${getUtcDateFormattedFullSmall(timeWindow?.[1], offset)}`}
            />
          )}
          {order?.Demand && <IdSearchDetail title={demand} text={order?.Demand.join(',')} />}
          {order?.Position && <IdSearchDetail title={position} text={order.Position} />}
          {order?.Territories && <IdSearchDetail title={territories} text={order.Territories} />}
          {order?.Skills && <IdSearchDetail title={skills} text={order.Skills.join(', ')} />}
          {activity?.typeId && <IdSearchDetail title={tourIdLabel} text={activity.typeId} />}
          {activity?.vehicleId && (
            <IdSearchDetail title={vehicleIdLabel} text={activity.vehicleId} />
          )}
          {activity?.type && <IdSearchDetail title={jobTypeLabel} text={activity.type} />}
          {hasLocation && (
            <IdSearchDetail
              title={locationLabel}
              text={`${activity.location.lat},${activity.location.lng}`}
            />
          )}
          {order?.Category && <IdSearchDetail title={category} text={order.Category} />}
          {order?.Priority && <IdSearchDetail title={priority} text={order.Priority} />}
        </StyledSummary>
      )}
    </div>
  );
};

export default withTranslation(IdSearchDetails);
